/**
 * MarkerEditor — interactive SVG with draggable core + delta markers
 * overlaid on the Gabor-skeleton backdrop.
 *
 * Tahap 1: drag + simpan posisi (no snap, no auto re-count).
 *
 * Coordinate model: SVG viewBox is 0 0 400 500 (matches preprocessing
 * TARGET dimensions; same space as DB core/deltas). The skeleton PNG is
 * 400×500. We convert mouse events to SVG coords via getScreenCTM() so
 * the drag works regardless of how big the SVG container is rendered.
 */
import { useEffect, useRef, useState } from "react";
import { auth } from "../api.js";
import { apiUrl } from "../lib/apiUrl.js";

const W = 400;
const H = 500;
const COLORS = {
  core: "#185FA5",
  delta: "#BA7517",
};

export interface Point {
  x: number;
  y: number;
}
export interface DeltaPoint extends Point {
  label: "kiri" | "kanan";
}

export interface MarkerGeometryChange {
  core?: Point | null;
  deltas?: DeltaPoint[];
}

interface MarkerEditorProps {
  skeletonUrl: string | null;
  core: Point | null;
  deltas: DeltaPoint[];
  canEdit: boolean;
  /**
   * Called when the user finishes dragging (after snap-to-skeleton).
   * Parent is expected to call the recount endpoint and save.
   */
  onChange?: (changes: MarkerGeometryChange) => void;
  /**
   * When true: don't draw the white background + skeleton bitmap inside
   * the SVG. Pixel buffer is still loaded from skeletonUrl for snap math.
   * Use this when overlaying markers on top of a different backdrop image
   * (e.g., the stitched landscape composite displayed below the SVG).
   */
  hideSkeleton?: boolean;
}

type DragTarget = { kind: "core" } | { kind: "delta"; index: number };

export function MarkerEditor({
  skeletonUrl,
  core,
  deltas,
  canEdit,
  onChange,
  hideSkeleton = false,
}: MarkerEditorProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [skelBlob, setSkelBlob] = useState<string | null>(null);
  const [skelErr, setSkelErr] = useState<string | null>(null);

  // Local optimistic state during a drag (so the marker visually follows
  // the cursor without round-tripping through the parent's onChange yet).
  const [localCore, setLocalCore] = useState<Point | null>(core);
  const [localDeltas, setLocalDeltas] = useState<DeltaPoint[]>(deltas);
  const dragRef = useRef<DragTarget | null>(null);

  // Sync from parent when not dragging (after a save returns new sub_version)
  useEffect(() => {
    if (!dragRef.current) setLocalCore(core);
  }, [core?.x, core?.y]);
  useEffect(() => {
    if (!dragRef.current) setLocalDeltas(deltas);
  }, [JSON.stringify(deltas)]);

  // Skeleton pixel buffer for snap-to-skeleton (Tahap 2). White bg, black ridges.
  // Stored as Uint8Array of luminance values; pixel < 128 → ridge.
  const skelPixelsRef = useRef<{ data: Uint8Array; w: number; h: number } | null>(null);

  // Fetch skeleton image with Bearer token (matches AuthedImage pattern).
  // Also draw to offscreen canvas → ImageData → pixel array for snap lookup.
  useEffect(() => {
    if (!skeletonUrl) {
      setSkelBlob(null);
      skelPixelsRef.current = null;
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    setSkelErr(null);
    (async () => {
      try {
        const token = auth.getToken();
        const res = await fetch(apiUrl(skeletonUrl), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          if (!cancelled) setSkelErr(`HTTP ${res.status}`);
          return;
        }
        const b = await res.blob();
        createdUrl = URL.createObjectURL(b);
        if (cancelled) {
          URL.revokeObjectURL(createdUrl);
          return;
        }
        setSkelBlob(createdUrl);

        // Decode to pixel array for snap-to-skeleton.
        try {
          const bmp = await createImageBitmap(b);
          const canvas = document.createElement("canvas");
          canvas.width = bmp.width;
          canvas.height = bmp.height;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(bmp, 0, 0);
            const img = ctx.getImageData(0, 0, bmp.width, bmp.height);
            // Convert RGBA → single-byte luminance.
            const buf = new Uint8Array(bmp.width * bmp.height);
            for (let i = 0, j = 0; i < img.data.length; i += 4, j += 1) {
              buf[j] = (img.data[i]! + img.data[i + 1]! + img.data[i + 2]!) / 3;
            }
            if (!cancelled) {
              skelPixelsRef.current = { data: buf, w: bmp.width, h: bmp.height };
            }
          }
          bmp.close?.();
        } catch (e) {
          // Snap will degrade gracefully if pixel decode fails.
          console.warn("MarkerEditor: skeleton pixel decode failed", e);
        }
      } catch (e) {
        if (!cancelled) setSkelErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [skeletonUrl]);

  /**
   * Snap a point to the nearest ridge pixel within `radius`. White background,
   * black ridges (pixel < 128 = ridge). Returns original point if no ridge in radius.
   */
  function snapToSkeleton(p: Point, radius = 12): Point {
    const skel = skelPixelsRef.current;
    if (!skel) return p;
    // Skeleton image is at W×H (SVG viewBox same dims). If decoded image size
    // differs (shouldn't), scale lookup coords proportionally.
    const sx = skel.w / W;
    const sy = skel.h / H;
    const cx = Math.round(p.x * sx);
    const cy = Math.round(p.y * sy);
    const rx = Math.round(radius * sx);
    const ry = Math.round(radius * sy);
    let bestX = -1, bestY = -1, bestDist = Infinity;
    const x0 = Math.max(0, cx - rx);
    const x1 = Math.min(skel.w - 1, cx + rx);
    const y0 = Math.max(0, cy - ry);
    const y1 = Math.min(skel.h - 1, cy + ry);
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        if (skel.data[y * skel.w + x]! >= 128) continue;
        const dx = x - cx;
        const dy = y - cy;
        const d = dx * dx + dy * dy;
        if (d < bestDist) {
          bestDist = d;
          bestX = x;
          bestY = y;
        }
      }
    }
    if (bestX < 0) return p;
    // Convert back to SVG-viewBox coords.
    return { x: Math.round(bestX / sx), y: Math.round(bestY / sy) };
  }

  /** Mouse → SVG-viewBox coordinates. */
  function toSvgPoint(evt: React.PointerEvent<SVGSVGElement> | PointerEvent): Point | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const local = pt.matrixTransform(ctm.inverse());
    return {
      x: Math.max(0, Math.min(W, Math.round(local.x))),
      y: Math.max(0, Math.min(H, Math.round(local.y))),
    };
  }

  function startDrag(target: DragTarget) {
    return (e: React.PointerEvent<SVGElement>) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      dragRef.current = target;
      (e.target as Element).setPointerCapture?.(e.pointerId);
    };
  }

  function onPointerMove(e: React.PointerEvent<SVGSVGElement>) {
    if (!dragRef.current) return;
    const p = toSvgPoint(e);
    if (!p) return;
    if (dragRef.current.kind === "core") {
      setLocalCore(p);
    } else {
      const i = dragRef.current.index;
      setLocalDeltas((prev) => {
        const next = prev.slice();
        const old = next[i];
        if (!old) return prev;
        // Re-label spatially based on new position vs current core.
        const ref = localCore ?? core;
        const label: "kiri" | "kanan" = ref ? (p.x < ref.x ? "kiri" : "kanan") : old.label;
        next[i] = { x: p.x, y: p.y, label };
        return next;
      });
    }
  }

  function onPointerUp(e: React.PointerEvent<SVGSVGElement>) {
    if (!dragRef.current) return;
    const target = dragRef.current;
    dragRef.current = null;
    (e.target as Element).releasePointerCapture?.(e.pointerId);

    // Tahap 2: snap-to-skeleton — find nearest ridge pixel in ~12px radius.
    // No ridge re-count here — that lives in the parent which calls the
    // /api/layer4/finger/recount endpoint (uses the SAME Gabor skeleton
    // as analyze.ts → counts stay consistent with the initial values).
    let nextCore = localCore;
    let nextDeltas = localDeltas;
    if (target.kind === "core" && localCore) {
      nextCore = snapToSkeleton(localCore);
      setLocalCore(nextCore);
    } else if (target.kind === "delta") {
      const i = target.index;
      const d = localDeltas[i];
      if (d) {
        const snapped = snapToSkeleton(d);
        const refCore = localCore ?? core;
        const label: "kiri" | "kanan" = refCore ? (snapped.x < refCore.x ? "kiri" : "kanan") : d.label;
        nextDeltas = localDeltas.slice();
        nextDeltas[i] = { x: snapped.x, y: snapped.y, label };
        setLocalDeltas(nextDeltas);
      }
    }

    if (onChange) {
      const changes: MarkerGeometryChange = {};
      if (target.kind === "core") changes.core = nextCore;
      else changes.deltas = nextDeltas;
      onChange(changes);
    }
  }

  // === Tahap 4: add / delete delta ===
  const [addMode, setAddMode] = useState(false);

  // ESC key exits add mode.
  useEffect(() => {
    if (!addMode) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setAddMode(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [addMode]);

  /** Place a new delta at the clicked SVG position (when in add mode). */
  function onSvgPointerDown(e: React.PointerEvent<SVGSVGElement>) {
    if (!addMode || !canEdit) return;
    // If a marker handled this (stopPropagation), this won't fire — so we're safe
    // to assume the click is on empty area.
    const p = toSvgPoint(e);
    if (!p) return;
    const snapped = snapToSkeleton(p);
    const refCore = localCore ?? core;
    const label: "kiri" | "kanan" = refCore ? (snapped.x < refCore.x ? "kiri" : "kanan") : "kanan";
    const newDelta: DeltaPoint = { x: snapped.x, y: snapped.y, label };
    const nextDeltas = [...localDeltas, newDelta];
    setLocalDeltas(nextDeltas);
    setAddMode(false);
    if (onChange) onChange({ deltas: nextDeltas });
  }

  /** Remove a delta by index. */
  function removeDelta(index: number) {
    if (!canEdit) return;
    const nextDeltas = localDeltas.filter((_, i) => i !== index);
    setLocalDeltas(nextDeltas);
    if (onChange) onChange({ deltas: nextDeltas });
  }

  if (skelErr) {
    return <span className="text-xs text-tabrakan">⚠ skeleton {skelErr}</span>;
  }

  return (
    <div className="relative w-full h-full">
    <svg
      ref={svgRef}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-full"
      style={{ touchAction: "none", userSelect: "none", cursor: addMode ? "crosshair" : undefined }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerDown={onSvgPointerDown}
    >
      {/* Background — opaque white + skeleton when shown; transparent click-capture
          rect so pointer events still fire in non-marker areas (for add-mode placement). */}
      <rect x="0" y="0" width={W} height={H} fill={hideSkeleton ? "transparent" : "#ffffff"} pointerEvents="all" />
      {!hideSkeleton && skelBlob && (
        <image href={skelBlob} x="0" y="0" width={W} height={H} preserveAspectRatio="xMidYMid meet" />
      )}

      {/* delta markers (yellow triangles) — render before core so core is on top */}
      {localDeltas.map((d, i) => {
        const size = 14;
        const pts = `${d.x},${d.y - size} ${d.x - size},${d.y + size} ${d.x + size},${d.y + size}`;
        return (
          <g key={`d-${i}`} style={{ cursor: canEdit ? "grab" : "default" }}>
            <polygon
              points={pts}
              fill="rgba(255,255,255,0.65)"
              stroke={COLORS.delta}
              strokeWidth={3.5}
              strokeLinejoin="round"
              onPointerDown={startDrag({ kind: "delta", index: i })}
            />
            <text
              x={d.x + 16}
              y={d.y + 16}
              fontSize="12"
              fontWeight={700}
              fontFamily="-apple-system,Segoe UI,Roboto,sans-serif"
              fill={COLORS.delta}
              stroke="#fff"
              strokeWidth={3}
              paintOrder="stroke"
              pointerEvents="none"
            >
              Δ {d.label === "kiri" ? "L" : "R"}
            </text>
            {/* Tahap 4: × delete button — only when canEdit. Positioned upper-left of triangle. */}
            {canEdit && (
              <g
                style={{ cursor: "pointer" }}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeDelta(i);
                }}
              >
                <circle cx={d.x - size - 4} cy={d.y - size - 4} r={9} fill="#fff" stroke="#E24B4A" strokeWidth={2} />
                <text
                  x={d.x - size - 4}
                  y={d.y - size - 1}
                  fontSize="12"
                  fontWeight={700}
                  fill="#E24B4A"
                  textAnchor="middle"
                  pointerEvents="none"
                >
                  ×
                </text>
              </g>
            )}
          </g>
        );
      })}

      {/* core marker (blue circle) */}
      {localCore && (
        <g style={{ cursor: canEdit ? "grab" : "default" }}>
          <circle
            cx={localCore.x}
            cy={localCore.y}
            r={18}
            fill="rgba(255,255,255,0.65)"
            stroke={COLORS.core}
            strokeWidth={3.5}
            onPointerDown={startDrag({ kind: "core" })}
          />
          <circle cx={localCore.x} cy={localCore.y} r={3} fill={COLORS.core} pointerEvents="none" />
          <text
            x={localCore.x + 22}
            y={localCore.y + 5}
            fontSize="13"
            fontWeight={700}
            fontFamily="-apple-system,Segoe UI,Roboto,sans-serif"
            fill={COLORS.core}
            stroke="#fff"
            strokeWidth={3}
            paintOrder="stroke"
            pointerEvents="none"
          >
            Core
          </text>
        </g>
      )}
    </svg>
    {/* Tahap 4: add-delta toggle button — overlay bottom-right of SVG. */}
    {canEdit && (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setAddMode((v) => !v);
        }}
        className={`absolute bottom-1 right-1 text-xs px-2 py-0.5 rounded font-medium shadow-sm border ${
          addMode
            ? "bg-amber-100 border-amber-400 text-amber-900"
            : "bg-white border-slate-300 text-slate-700 hover:border-amber-400 hover:text-amber-900"
        }`}
        title={addMode ? "Klik di gambar untuk taruh delta (Esc untuk batal)" : "Tambah delta baru"}
      >
        {addMode ? "Klik posisi delta… (Esc)" : "+ Δ"}
      </button>
    )}
    </div>
  );
}
